import "./AssignmentForm.less";
import * as React from "react";
import { AtAsyncAutoCompleteSelectField, BaseColumn, BaseForm, BaseItemList, ModalButtons } from "../../components";
import { AtButton } from "@atman/design-system";
import { IReactSelectOptionObject } from "@atman/business";
import { IReactionDisposer, action, observable, reaction, runInAction } from "mobx";
import { observer } from "mobx-react";

export interface IAssignmentFormProps {
    loadItems: () => Promise<IReactSelectOptionObject[]>;
    loadOptions: (inputValue: string) => Promise<IReactSelectOptionObject[]>;
    onSave: (itemIds: string[]) => Promise<void>;
    selectFieldPlaceholder: string;
    selectKey?: string;
}

@observer
export class AssignmentForm extends BaseForm<IAssignmentFormProps> {
    @observable private selectedItemIds: IReactSelectOptionObject[] = [];

    loadItemsReactionDisposer: IReactionDisposer = reaction(
        () => this.props.loadItems,
        () => this._loadItems(),
    );

    componentDidMount() {
        this._loadItems();
    }

    componentWillUnmount(): void {
        this.loadItemsReactionDisposer();
    }

    @action private async _loadItems() {
        const { loadItems } = this.props;

        const result = await loadItems();

        runInAction(() => {
            this.selectedItemIds = result;
        });
    }

    @action private options = async (inputValue: string) => {
        const { loadOptions } = this.props;

        return await this.loadAsyncSelectOptions("assignment-modal", async () => {
            const results = await loadOptions(inputValue);

            return results.filter((x) => !this.selectedItemIds.some((i) => i.value === x.value));
        });
    };

    @action private addItem = (option: IReactSelectOptionObject) => {
        if (!this.selectedItemIds.find((x) => x.value === option.value)) {
            this.selectedItemIds.push(option);
        }
    };

    @action private removeItem = (itemId: string) => {
        const itemIndex = this.selectedItemIds.findIndex((x) => x.value === itemId);

        if (itemIndex >= 0) {
            this.selectedItemIds.splice(itemIndex, 1);
        }
    };

    onSave = async () => {
        const { onSave } = this.props;

        this.setIsLoading(true);

        await onSave(this.selectedItemIds.map((x) => x.value));

        this.setIsLoading(false);
    };

    render() {
        const { selectFieldPlaceholder, selectKey } = this.props;

        const tableColumns: BaseColumn[] = [new LabelColumn(), new ButtonColumn(this.removeItem)];

        return (
            <>
                <AtAsyncAutoCompleteSelectField
                    loadOptions={this.options}
                    value={null}
                    onChange={this.addItem}
                    placeholder={selectFieldPlaceholder}
                    defaultOptions
                    cacheOptions
                    selectKey={selectKey}
                />
                <BaseItemList
                    tableColumns={tableColumns}
                    tableRows={this.selectedItemIds.slice()}
                    keyFieldOverride={"value"}
                />
                <ModalButtons saveAction={this.onSave} isLoading={this.isLoading} />
            </>
        );
    }
}

class LabelColumn extends BaseColumn {
    constructor() {
        super();

        this.dataField = "label";
        this.text = "global.name".localize();
    }
}

class ButtonColumn extends BaseColumn {
    constructor(removeItemAction: (id: string) => void) {
        super();

        this.setAsButtonColumn();
        this.dataField = "value";
        this.formatter = (cell) => (
            <span>
                <AtButton onClick={() => removeItemAction(cell)} color={"ghost"} size={"md"}>
                    {"clientApp.remove".localize()}
                </AtButton>
            </span>
        );
    }
}
